import type { Address } from "viem"
import type { Config } from "wagmi"
import { readContract } from "@wagmi/core"

import { thickNfpPositionStateAbi } from "@/domains/abis/thick-v1-nfpm-abi"

/**
 * Minimal ABI fragment for enumerating NFPM NFTs held by an owner and
 * reading each one's `positions(tokenId)` tuple. Deliberately narrow so we
 * don't pull the full NFPM ABI into a read-only lens.
 */
const thickV1NfpmDiscoveryAbi = [
  {
    inputs: [{ internalType: "address", name: "owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "owner", type: "address" },
      { internalType: "uint256", name: "index", type: "uint256" },
    ],
    name: "tokenOfOwnerByIndex",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  ...thickNfpPositionStateAbi,
] as const

export type DiscoveredNfpmPosition = {
  tokenId: bigint
  token0: Address
  token1: Address
  fee: number
  tickLower: number
  tickUpper: number
  liquidity: bigint
}

/**
 * Result envelope for {@link discoverUserNfpmPositions}.
 *
 * `positions` is the flat list every caller needs. `totalOwned` and
 * `scanned` let the orchestrator surface a user-visible banner when we
 * truncated the scan — previously this was a silent `console.warn`, which
 * meant wallets with huge NFPM holdings lost positions without any UI
 * feedback. Callers that don't care about truncation simply read
 * `result.positions`.
 */
export type DiscoverUserNfpmPositionsResult = {
  positions: DiscoveredNfpmPosition[]
  totalOwned: number
  scanned: number
  truncated: boolean
}

export const DISCOVER_USER_NFPM_MAX_SCAN = 500

/**
 * Enumerate every NFPM-owned NFT belonging to `account` and fetch the
 * `positions(tokenId)` tuple for each. The returned list is the ground
 * truth for mapping indexer `Position` rows to real NFPM tokenIds, which
 * the indexer does not surface until Proposal 4 ships.
 *
 * Matching strategy (caller responsibility): for each indexer Position,
 * pick the discovered NFT where `(token0, token1, fee, tickLower, tickUpper)`
 * matches the indexer tuple. In the rare case where multiple NFTs share the
 * same tuple (historical burns + re-mints), prefer the one with non-zero
 * `liquidity`; falling back to the highest tokenId if all are zero.
 *
 * This call issues `1 + N + N` RPC reads where N is the number of NFTs held.
 * Reads are intentionally sequential to avoid provider-side multicall packing
 * that can exceed gas/time limits on some RPCs (Pocket Base in particular).
 * Callers that need extra throughput should chunk at the transport layer.
 */
export async function discoverUserNfpmPositions({
  account,
  nfpManagerAddress,
  chainId,
  config,
}: {
  account: Address
  nfpManagerAddress: Address
  chainId: number
  config: Config
}): Promise<DiscoverUserNfpmPositionsResult> {
  const balance = (await readContract(config, {
    abi: thickV1NfpmDiscoveryAbi,
    address: nfpManagerAddress,
    chainId,
    functionName: "balanceOf",
    args: [account],
  })) as bigint

  if (balance === 0n) {
    return { positions: [], totalOwned: 0, scanned: 0, truncated: false }
  }

  const balanceCount = Number(balance)
  if (!Number.isFinite(balanceCount) || balanceCount <= 0) {
    return { positions: [], totalOwned: 0, scanned: 0, truncated: false }
  }
  const limitedCount = Math.min(balanceCount, DISCOVER_USER_NFPM_MAX_SCAN)
  const truncated = balanceCount > DISCOVER_USER_NFPM_MAX_SCAN
  if (truncated) {
    console.warn(
      `[discoverUserNfpmPositions] Owner has ${balanceCount} NFTs; scanning first ${DISCOVER_USER_NFPM_MAX_SCAN} to avoid RPC overload.`,
    )
  }

  const tokenIds: bigint[] = []
  for (let i = 0; i < limitedCount; i++) {
    const tokenId = (await readContract(config, {
      abi: thickV1NfpmDiscoveryAbi,
      address: nfpManagerAddress,
      chainId,
      functionName: "tokenOfOwnerByIndex",
      args: [account, BigInt(i)],
    })) as bigint
    tokenIds.push(tokenId)
  }

  const positions: DiscoveredNfpmPosition[] = []
  for (const tokenId of tokenIds) {
    const tuple = (await readContract(config, {
      abi: thickV1NfpmDiscoveryAbi,
      address: nfpManagerAddress,
      chainId,
      functionName: "positions",
      args: [tokenId],
    })) as readonly [
      bigint,
      Address,
      Address,
      Address,
      number,
      number,
      number,
      bigint,
      bigint,
      bigint,
      bigint,
      bigint,
    ]
    positions.push({
      tokenId,
      token0: tuple[2],
      token1: tuple[3],
      fee: Number(tuple[4]),
      tickLower: Number(tuple[5]),
      tickUpper: Number(tuple[6]),
      liquidity: tuple[7],
    })
  }

  return {
    positions,
    totalOwned: balanceCount,
    scanned: limitedCount,
    truncated,
  }
}
